'use strict';

/**
 * @prosecaptain/sentiment-rules
 *
 * Single source of truth for the sentiment pipeline identity and the session
 * counting rules shared by:
 *   - frontend (dashboard, widget stats, admin analytics),
 *   - servers/sentiment-analysis (the judge worker writes this version),
 *   - servers/mailer-core (monthly / weekly reports).
 *
 * Plain CommonJS, no dependencies, no syntax newer than ES2017, so it loads in
 * Node (require and import via index.mjs), in Jest and in CRA webpack without
 * extra transpilation.
 *
 * Bumping the judge engine = changing CURRENT_PIPELINE_VERSION below. Nothing
 * else in the repo may contain a `judge.ps.vN` literal (enforced by
 * scripts/check-single-source.sh).
 */

// Bumped 2026-09-08 together with the judge model (gpt-5-mini -> gpt-5.6-luna).
const CURRENT_PIPELINE_VERSION = 'judge.ps.v3';

const DEFAULT_TIME_ZONE = 'Europe/Warsaw';

const isCurrentPipeline = (version) => version === CURRENT_PIPELINE_VERSION;

const getSessionPipelineVersion = (session) => (session && session.pipeline_version) || null;

const isCurrentPipelineSession = (session) => isCurrentPipeline(getSessionPipelineVersion(session));

/**
 * Judge confidence usable in stats: a real number in (0, 1].
 * `null` means the judge did not run (skip / quota), `0` is a placeholder.
 */
const hasJudgeConfidence = (session) => {
  const raw = session ? session.confidence : null;
  if (raw === null || raw === undefined || raw === '') return false;
  const value = typeof raw === 'number' ? raw : parseFloat(raw);
  return Number.isFinite(value) && value > 0 && value <= 1;
};

/**
 * A session counts toward judge statistics only when it was scored by the
 * current pipeline and the judge actually produced a confidence (> 0).
 */
const isScoredSession = (session) => isCurrentPipelineSession(session) && hasJudgeConfidence(session);

const getResolutionStatus = (session) => {
  const fulfilled = session ? session.intent_fulfilled : undefined;
  if (fulfilled === 'true') return 'resolved';
  if (fulfilled === 'partial') return 'partially_resolved';
  if (fulfilled === 'false') return 'unresolved';
  return (session && session.resolution_status) || null;
};

const getSessionPriority = (session) => (
  (session && (session.priority_band || session.business_risk)) || null
);

const formatterCache = new Map();

const getFormatter = (timeZone) => {
  if (!formatterCache.has(timeZone)) {
    formatterCache.set(timeZone, new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }));
  }
  return formatterCache.get(timeZone);
};

/**
 * Calendar bucket of a timestamp in a given time zone.
 * granularity: 'month' -> 'YYYY-MM', 'day' -> 'YYYY-MM-DD'.
 * Returns null for unparseable input.
 */
const toPeriodKey = (value, options) => {
  const opts = options || {};
  const timeZone = opts.timeZone || DEFAULT_TIME_ZONE;
  const granularity = opts.granularity || 'month';
  const date = value instanceof Date ? value : new Date(value);
  if (value === null || value === undefined || Number.isNaN(date.getTime())) return null;
  const parts = {};
  getFormatter(timeZone).formatToParts(date).forEach((part) => {
    parts[part.type] = part.value;
  });
  if (granularity === 'day') return `${parts.year}-${parts.month}-${parts.day}`;
  return `${parts.year}-${parts.month}`;
};

/**
 * Counts user messages by the date of the MESSAGE, not of the session: a
 * visitor returning in September to a session opened in August adds to
 * September. Assistant and system messages are ignored.
 *
 * @param {Array<{role: string, date_created: string}>} messages chat_messages rows
 * @param {{timeZone?: string, granularity?: 'month'|'day', dateField?: string}} [options]
 * @returns {Object<string, number>} e.g. { '2026-09': 61, '2026-10': 5 }
 */
const countUserMessagesByMessageDate = (messages, options) => {
  const opts = options || {};
  const dateField = opts.dateField || 'date_created';
  const counts = {};
  (Array.isArray(messages) ? messages : []).forEach((message) => {
    if (!message || message.role !== 'user') return;
    const key = toPeriodKey(message[dateField], opts);
    if (!key) return;
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
};

/**
 * Primary key of a widget_events_daily row: `${linked_workspace}:${widget_id || '-'}:${day}`.
 * Directus cannot create composite unique indexes through the API, so the
 * (linked_workspace, widget_id, day) uniqueness lives in this string key.
 * Must stay byte-identical with servers/sentiment-analysis/scripts/directus/analytics-schema.js.
 */
const BUCKET_NO_WIDGET = '-';

const bucketKey = (linkedWorkspace, widgetId, day) => {
  const ws = Number(linkedWorkspace);
  if (!Number.isInteger(ws) || ws <= 0) {
    throw new TypeError(`bucketKey: linked_workspace must be a positive integer, got ${linkedWorkspace}`);
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(day))) {
    throw new TypeError(`bucketKey: day must be YYYY-MM-DD, got ${day}`);
  }
  const widget = widgetId === null || widgetId === undefined || widgetId === '' ? BUCKET_NO_WIDGET : String(widgetId);
  if (widget.indexOf(':') !== -1) {
    throw new TypeError('bucketKey: widget_id must not contain ":"');
  }
  return [ws, widget, day].join(':');
};

module.exports = {
  CURRENT_PIPELINE_VERSION,
  DEFAULT_TIME_ZONE,
  isCurrentPipeline,
  getSessionPipelineVersion,
  isCurrentPipelineSession,
  hasJudgeConfidence,
  isScoredSession,
  getResolutionStatus,
  getSessionPriority,
  toPeriodKey,
  countUserMessagesByMessageDate,
  BUCKET_NO_WIDGET,
  bucketKey
};
